/**
 * One background refresh of a lookup enum's cached key→label pairs, plus the
 * throttle that keeps every caller of it bounded.
 *
 * Four surfaces refresh the same cache for four different reasons:
 *   - `'miss'`         — a grid cell whose key isn't in the map (`useLookupLabelSelfHeal`),
 *   - `'swr'`          — a picker / editor opened over a stale cache (`useEnumValues`),
 *   - `'write-through'`— someone just edited the target table (`useLookupCacheWriteThrough`),
 *   - `'declare'`      — a declaration that was just created outside the declare
 *                        dialog (the loose-FK editor's "Also treat as enum"
 *                        checkbox), which has no pairs at all yet.
 *
 * They share this module so the fetch shape (live label-column resolution →
 * bounded pair fetch → MetaInfo write-through → invalidate) can never drift
 * apart between them, and so they share ONE throttle map: a column that just
 * healed itself must not be re-fetched by the next grid mounting beside it.
 *
 * `force` is the exception the write-through path needs: an explicit refresh
 * *after a known write* is evidence the cache is wrong, so it must not be
 * swallowed by a recent miss-driven attempt. It bypasses the time window only
 * — never the in-flight guard, which exists so one column can't have two
 * fetches racing to write the same cache row.
 */

import type { QueryClient } from '@tanstack/react-query';
import type { DatabaseMetaInfo, EnumLookupTarget } from '@/types/electron';
import type { TrackFunction } from '../../../../core/analytics/analyticsTypes';
import { fetchEnumValues } from '../services/metaInfoApi';
import {
  LOOKUP_TARGET_COLUMNS_STALE_MS,
  fetchLookupTargetColumns,
  lookupTargetColumnsQueryKey,
  resolveLookupLabelColumn,
} from './lookupLabelColumn';
import { hostMetadataQueryKey, type TableExplorerMetadataEditorProviders } from '../../table-explorer/components/tableExplorerHostPorts';

/**
 * How long a column waits before it may refresh again. Long enough that a grid
 * full of unknown keys (a target table that legitimately doesn't contain them)
 * can never turn into a query loop; short enough that a user who just added the
 * missing row sees the label after a re-open.
 */
export const LOOKUP_REFRESH_THROTTLE_MS = 5 * 60 * 1000;

/** Why a refresh ran — reported as the `reason` of `feature:enum_values_refresh`. */
export type LookupRefreshReason = 'miss' | 'swr' | 'write-through' | 'declare';

/**
 * Module-level, deliberately: tab remounts, grid re-mounts and the two grids
 * side by side must all share one throttle, or every remount would stampede
 * the target table. Connection-scoped, so parallel e2e runs (unique connection
 * ids) never see each other's attempts.
 */
const lastAttemptByKey = new Map<string, number>();
/** Keys with a refresh in flight — at most one per column at a time. */
const inFlightKeys = new Set<string>();

export const lookupRefreshKey = (
  connectionId: string,
  database: string,
  schema: string,
  table: string,
  column: string,
) => `${connectionId}|${database}|${schema}|${table}|${column}`;

/** Test-only: forget every recorded attempt (unit tests, e2e resets). */
export function __resetLookupRefreshThrottleForTests(): void {
  lastAttemptByKey.clear();
  inFlightKeys.clear();
}

/**
 * Take the column's refresh slot, recording the attempt BEFORE any awaiting so
 * a re-render during the fetch can't open a second one. `false` = someone else
 * holds it (in flight) or the window hasn't elapsed.
 */
export function claimLookupRefresh(key: string, options?: { force?: boolean }): boolean {
  if (inFlightKeys.has(key)) return false;
  if (!options?.force) {
    const lastAttempt = lastAttemptByKey.get(key);
    if (lastAttempt !== undefined && Date.now() - lastAttempt < LOOKUP_REFRESH_THROTTLE_MS) {
      return false;
    }
  }
  lastAttemptByKey.set(key, Date.now());
  inFlightKeys.add(key);
  return true;
}

/** Release a slot claimed by `claimLookupRefresh` (the attempt stays recorded). */
export function releaseLookupRefresh(key: string): void {
  inFlightKeys.delete(key);
}

/**
 * Re-read one lookup column's key→label pairs and write them through to
 * MetaInfo. Resolves when the attempt is over — it never throws and never
 * reports failure: a dropped target table, a revoked grant or a disconnected
 * session must not break the surface the user is looking at.
 */
export async function refreshLookupCacheForColumn(args: {
  queryClient: QueryClient;
  connectionId: string;
  database: string;
  engine: string;
  /** The DECLARED column whose pair cache is refreshed. */
  schema: string;
  table: string;
  column: string;
  /** The FK target the pairs are read from. */
  target: EnumLookupTarget;
  /**
   * Read at fetch time, not at call time — the label column comes from the
   * freshest MetaInfo snapshot available when the query actually goes out.
   */
  getMetaInfo: () => DatabaseMetaInfo | null | undefined;
  reason: LookupRefreshReason;
  /** Skip the throttle window (an explicit refresh after a known write). */
  force?: boolean;
  track?: TrackFunction;
  /** Re-read MetaInfo so every subscriber picks up the fresh pairs. */
  invalidate: (connectionId: string, database: string) => void;
  /**
   * Host metadata provider (Azure Table, S4 review C, M7): the target's
   * columns come from it, never from the SQL metadata IPC. Absent = SQL.
   */
  metadataEditors?: TableExplorerMetadataEditorProviders;
}): Promise<void> {
  const {
    queryClient,
    connectionId,
    database,
    engine,
    schema,
    table,
    column,
    target,
    getMetaInfo,
    reason,
    force,
    track,
    invalidate,
    metadataEditors,
  } = args;

  const key = lookupRefreshKey(connectionId, database, schema, table, column);
  if (!claimLookupRefresh(key, { force })) return;

  try {
    // Same query key the loose-FK target picker and the declare dialog use, so
    // a background refresh reuses whatever the UI just fetched (and vice versa)
    // instead of issuing its own metadata round-trip.
    const targetColumns = metadataEditors
      ? await queryClient.fetchQuery({
          queryKey: hostMetadataQueryKey(metadataEditors, connectionId, 'lookup-target-columns', database, target.toSchema, target.toTable),
          queryFn: async () => (await metadataEditors.listColumns({
            connectionId,
            database,
            schema: target.toSchema ?? '',
            table: target.toTable,
          })).map((targetColumn) => targetColumn.name),
          staleTime: LOOKUP_TARGET_COLUMNS_STALE_MS,
        })
      : await queryClient.fetchQuery({
          queryKey: lookupTargetColumnsQueryKey(
            connectionId,
            database,
            target.toSchema,
            target.toTable,
          ),
          queryFn: () =>
            fetchLookupTargetColumns({
              connectionId,
              database,
              schema: target.toSchema,
              table: target.toTable,
            }),
          staleTime: LOOKUP_TARGET_COLUMNS_STALE_MS,
        });
    const labelColumn = resolveLookupLabelColumn({
      metaInfo: getMetaInfo(),
      schema: target.toSchema,
      table: target.toTable,
      keyColumn: target.toColumn,
      columns: targetColumns,
    });

    await fetchEnumValues(
      connectionId,
      database,
      schema,
      table,
      column,
      engine,
      'lookup',
      undefined,
      undefined,
      {
        toSchema: target.toSchema,
        toTable: target.toTable,
        toColumn: target.toColumn,
        labelColumn,
      },
    );

    track?.('feature:enum_values_refresh', { engine, source: 'lookup', reason });
    // The IPC wrote the fresh pairs through to MetaInfo — re-read it so the
    // grids' label maps (and every other subscriber) pick them up.
    invalidate(connectionId, database);
  } catch {
    // Swallowed by design (see the doc comment). The attempt is already
    // recorded, so a broken target costs one query per throttle window.
  } finally {
    releaseLookupRefresh(key);
  }
}
